<template>
  <div v-if="activeBreedings.length > 0" class="space-y-4">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <h3 :class="`text-lg font-semibold ${applyThemeClasses('text.primary')}`">
        🐣 Your Active Breedings ({{ activeBreedings.length }})
      </h3>
      <button 
        @click="collapsed = !collapsed"
        :class="`text-sm ${applyThemeClasses('form.button.outline')}`"
      >
        {{ collapsed ? 'Show' : 'Hide' }}
      </button>
    </div>

    <!-- Breeding Cards -->
    <div v-if="!collapsed" class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div 
        v-for="breeding in activeBreedings" 
        :key="breeding.id"
        :class="`rounded-lg p-4 border ${applyThemeClasses('layout.card')}`"
      >
        <!-- Parent Info -->
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center space-x-2">
            <span class="text-sm font-medium">{{ breeding.motherName }}</span>
            <span class="text-gray-400">×</span>
            <span class="text-sm font-medium">{{ breeding.fatherName }}</span>
          </div>
          <div :class="`px-2 py-1 rounded-full text-xs font-medium ${getStageInfo(breeding.stage).color}`">
            {{ getStageInfo(breeding.stage).icon }} {{ getStageInfo(breeding.stage).text }}
          </div>
        </div>

        <!-- Progress Bar -->
        <div class="mb-3">
          <div class="flex justify-between text-xs text-gray-500 mb-1">
            <span>Progress</span>
            <span>{{ daysElapsed(breeding) }}/14 days</span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-3 relative overflow-hidden">
            <div 
              :class="`h-3 rounded-full transition-all duration-500 ${getProgressBarColor(breeding.stage)}`"
              :style="{ width: `${(daysElapsed(breeding) / 14) * 100}%` }"
            ></div>
            <!-- Stage markers -->
            <div class="absolute top-0 left-0 w-full h-full flex">
              <div class="w-6/14 border-r border-white/30"></div>
              <div class="w-3/14 border-r border-white/30"></div>
              <div class="w-5/14"></div>
            </div>
          </div>
          <div class="flex justify-between text-xs text-gray-400 mt-1">
            <span>🥚</span>
            <span>🐣</span>
            <span>🐤</span>
          </div>
        </div>

        <!-- Days Remaining -->
        <div class="text-center">
          <div :class="`text-lg font-bold ${applyThemeClasses('text.accent')}`">
            {{ daysRemaining(breeding) }}
          </div>
          <div :class="`text-xs ${applyThemeClasses('text.secondary')}`">
            {{ daysRemaining(breeding) === 1 ? 'day' : 'days' }} remaining
          </div>
        </div>
      </div>
    </div>

    <!-- Collapsed Summary -->
    <div v-if="collapsed" :class="`text-sm ${applyThemeClasses('text.secondary')}`">
      {{ activeBreedings.length }} chocobo{{ activeBreedings.length === 1 ? '' : 's' }} breeding
      <span v-if="nextCompletion">
        • Next completion in {{ nextCompletion }} day{{ nextCompletion === 1 ? '' : 's' }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useGameStore } from '@/store/game'
import { BreedingManager } from '@/services/breedingManager'
import { applyThemeClasses } from '@/utils/themeColors'

const gameStore = useGameStore()
const collapsed = ref(false)

const activeBreedings = computed(() => {
  // Only show player's breedings on home page
  return (gameStore.activeBreedings || []).filter(breeding => !breeding.isNPC)
})

const getStageInfo = (stage) => {
  return BreedingManager.getStageDisplayInfo(stage)
}

const daysElapsed = (breeding) => {
  return Math.min(14, gameStore.currentDay - breeding.startDay)
}

const daysRemaining = (breeding) => {
  return Math.max(0, breeding.endDay - gameStore.currentDay)
}

const nextCompletion = computed(() => {
  if (activeBreedings.value.length === 0) return null
  
  const soonest = activeBreedings.value.reduce((min, breeding) => {
    const remaining = daysRemaining(breeding)
    return remaining < min ? remaining : min
  }, 14)
  
  return soonest
})

const getProgressBarColor = (stage) => {
  const colors = {
    incubating: 'bg-gradient-to-r from-yellow-300 to-yellow-500',
    hatching: 'bg-gradient-to-r from-orange-400 to-orange-600',
    nursing: 'bg-gradient-to-r from-green-400 to-green-600'
  }
  return colors[stage] || colors.incubating
}
</script>